<template>
  <div
    class="relative deck"
    data-testid="deck"
    :class="{
      participant: userType === 'controlled',
      open: isSidebarOpen && userType !== 'controlled',
    }"
    :style="slide && 'SM'.includes(slide.type) ? { touchAction: 'none' } : {}"
  >
    <VAlert
      class="absolute"
      :is-visible="alerts.isAlertVisible"
      :timeout="alerts.alertTimeout"
      :banner-type="alerts.alertType"
      @clear-banner="hideAlert"
    >
      <p class="mb-0">{{ alerts.alertMessage }}</p>
    </VAlert>
    <VModal v-if="noWebGLDetected" v-model:showing="noWebGLDetected" class="fixed" data-testid="no-webgl-detected-modal">
      <div class="max-w-md">
        <p class="mb-4 text-lg font-medium text-gray-900">WebGL is disabled on your browser</p>
        <p class="mb-8 text-sm text-gray-700">
          VRIFY requires WebGL to be enabled in your browser to see 360 images and 3D models. Please contact your IT department to have it
          re-enabled.
        </p>
        <VButton class="block ml-auto" size="lg" variant="copper" @click="noWebGLDetected = false">Okay</VButton>
      </div>
    </VModal>
    <DeckSlides
      v-if="userType !== 'controlled'"
      class="absolute z-10 h-full sidebar-transition"
      :deck="deck"
      :meeting="meeting"
      :user="user"
      :show-autoplay="!meeting && !embedDeck"
      :current-slide-index="currentSlideIndex"
      :is-sidebar-open="isSidebarOpen"
      :analytics-event-name="analyticsEventName"
      :share-code="meeting && meeting.share_id"
      :embed-deck="embedDeck"
      :non-shareable-meeting="nonShareableMeeting"
      @set-current-slide-index="($emit('set-current-slide-index', $event), annotations.reset())"
      @start-auto-play="$emit('start-auto-play')"
    />
    <div
      class="absolute left-0 z-0 w-full h-full overflow-hidden select-none content"
      data-testid="deck-content"
      :class="contentBackground"
    >
      <DeckOverlay
        class="z-30 layer"
        :is-sidebar-open="isSidebarOpen"
        :deck="deck"
        :current-slide-index="currentSlideIndex"
        :first-valid-slide="firstValidSlide"
        :last-valid-slide="lastValidSlide"
        :analytics-event-name="analyticsEventName"
        :embed-deck="embedDeck"
        :is-meeting="!!meeting"
        :is-av-session="meeting ? meeting.is_audio_video_enabled : false"
        :is-content-being-shared="isContentBeingShared"
        :is-grid-mode="isGridMode"
        :is-auto-playing="isAutoPlaying"
        :is-slide-transitioning="isSlideTransitioning"
        :deck-user-type="userType"
        :site-hotspot-type="siteHotspotType"
        :site-history="site.siteHistory"
        :tile-row-size="tileRowSize"
        @toggle-sidebar="isSidebarOpen = !isSidebarOpen"
        @dismiss-embed-overlay="$emit('dismiss-embed-overlay')"
        @toggle-fullscreen="onToggleFullscreen"
        @navigation-previous-slide="$emit('navigation-previous-slide')"
        @navigation-next-slide="$emit('navigation-next-slide')"
        @exit-deck="handleExitDeck"
      />
      <div class="z-0 layer slide-container" :inert="userType === 'controlled'">
        <transition name="fade" mode="out-in">
          <div v-if="!slide" data-testid="empty-slide" />
          <DeckImageSlide
            v-else-if="slide.type === 'I'"
            :slide="slide as unknown as SlideImage"
            :content-style="panZoomTransform.css"
            @slide-ready="onSlideReady"
            @update:image-dimensions="$emit('update:image-dimensions', $event)"
            @update:annotations="Object.assign(annotations, $event)"
          />
          <DeckSiteSlide
            v-else-if="slide.type === 'S'"
            :slide="slide"
            :user="user"
            :site="site"
            :is-admin="user && (user.is_super_admin || (user.companies && user.companies.some(c => c.id === deck.company.id)))"
            :image-hotspot-pan-zoom-transform="panZoomTransform"
            :is-image-hotspot-description-open="isImageHotspotDescriptionOpen"
            :current-pdf-page="currentPdfPage"
            :slide-refresh-key="slideRefreshKey"
            :uuid="uuid"
            :tile-row-size="tileRowSize"
            :three-d-viewer="threeDViewer"
            @goto:hotspot="onGoToHotspot"
            @toggle-image-hotspot-description="$emit('toggle-image-hotspot-description')"
            @camera-change="$emit('site-camera-change', $event)"
            @camera-ready="onSlideReady"
            @update:site-observer="$emit('update:site-observer', $event)"
            @update:annotations="Object.assign(annotations, $event)"
            @update:site-camera="$emit('update:site-camera', $event)"
            @update:image-dimensions="$emit('update:image-dimensions', $event)"
            @3d-frame-change="reset"
            @3d-camera-ready="onSlideReady"
            @update:animating="animating = $event"
            @update:3d-load-status="isSlideLoading = $event"
            @update:3d-animating="animating = $event"
          />
          <DeckThreeDSlide
            v-else-if="slide.type === 'M'"
            v-model:animating="animating"
            :is-sidebar-open="isSidebarOpen"
            :slide="slide"
            :uuid="uuid"
            :deck-id="deck.id"
            :company-id="deck.company.id"
            :user="user"
            :slide-refresh-key="slideRefreshKey"
            :three-d-viewer-error="threeDViewerError"
            :three-d-viewer="threeDViewer"
            @update:animating="animating = $event"
            @frame-change="reset"
            @camera-ready="onSlideReady"
            @update:aspect="annotations.aspect = $event"
            @update:load-status="isSlideLoading = $event"
            @update:transitioning="isSlideTransitioning = $event"
          />
          <DeckPdfSlide
            v-else-if="slide.type === 'P'"
            :slide="slide"
            :content-style="panZoomTransform.css"
            :slide-refresh-key="slideRefreshKey"
            @slide-ready="onSlideReady"
            @refetch-file="refetchFile"
            @update:image-dimensions="$emit('update:image-dimensions', $event)"
            @update:annotations="Object.assign(annotations, $event)"
          />
          <DeckDynamicSlide
            v-else-if="slide.type === 'D'"
            :url="`${slide.content_url}`"
            :is-auto-playing="isAutoPlaying"
            @slide-ready="onSlideReady"
          />
          <DeckVideoSlide
            v-else-if="slide.type === 'V'"
            :slide="slide"
            :autoplay="isAutoPlaying"
            @pause-autoplay="$emit('pause-autoplay')"
            @resume-autoplay="$emit('resume-autoplay')"
          />
          <h2 v-else data-testid="slide-fallback-title">{{ slide.title }}</h2>
        </transition>
      </div>
      <DeckCaptions
        v-if="captionsEnabled"
        :show="showCaptions"
        :slide="slide"
        :slide-refresh-key="slideRefreshKey"
        :no-transition="isSlideLoading"
        :is-sidebar-open="isSidebarOpen"
      />
      <DeckAnnotations
        class="z-20 layer"
        v-bind="annotations"
        :user="user"
        :is-sidebar-open="isSidebarOpen"
        :disabled="annotationsDisabled"
        :object-fit="annotationsObjectFit"
        :render-to-portal="renderAnnotationsToPortal"
        @toggle-active="annotations.active = !annotations.active"
        @annotate="$emit('annotate', $event)"
        @update="Object.assign(annotations, $event)"
        @subscribe="$emit('update:annotationSubscriber', $event)"
      />
    </div>
    <div v-if="isContentBeingChosen" class="screen-share-overlay" data-testid="screen-share-overlay" />
    <VContextMenu class="z-50" :context-menu="contextMenu as ContextMenu" @close="contextMenu = { ...contextMenu, show: false }" />
  </div>
</template>

<script lang="ts" setup>
import type { Deck, DeckUser, IncludeCompany, SlideImage, SlideType } from '@/entities/deck'
import { useSetAnnotationsFromPanZoom } from '@/composables/useSetAnnotationsFromPanZoom'
import type { Meeting, ScheduleSetting } from '@/entities/meeting'
import type { SelectedHotspot } from '@/entities/site'
import type { SiteState } from '@/composables/useDeck'
import type { PanZoomTransform } from '@/composables/useImagePanZoom'
import type { AnnotationColors } from '@/lib/annotations/constants'
import type { TileRowSize } from '@/composables/useTileRowSize'
import type { DeckWithEmbed } from '@/entities/embed'
import type { ReadonlyControls } from '@/lib/three-d-resources/controls/readonly-controls'
import type { UseThreeDViewer } from '@/composables/useThreeDViewer'
import usePreloadPdfImages from '@/composables/usePreloadPdfImages'
import usePreloadSiteImages from '@/composables/usePreloadSiteImages'
import { useContextMenu } from '~/composables/useContextMenu'
import { useDetectWebGL } from '@/composables/useDetectWebGL'
import type { DeepReadonly } from 'vue'
import { RemoteControlMessagesOut } from '~/composables/useDeckRecorder'
import type Record from '~/components/icons/Record.svg'

const config = useRuntimeConfig()

const props = defineProps({
  analyticsEventName: {
    type: String as PropType<
      'Deck' | 'EmbedDeck' | 'AnimatedEmbedDeck' | 'PartnerEmbed' | 'AVMeetingDeck' | 'NonAVMeetingDeck' | 'ModalDeck' | 'VideoRecording'
    >,
    required: true,
  },
  analyticsHaveFired: {
    type: Object as PropType<{ teaserPageLoad: boolean; teaserClickViewPresentation: boolean; deckPageLoad: boolean }>,
    default: () => {},
  },
  deck: {
    type: Object as PropType<DeepReadonly<Deck & IncludeCompany>>,
    required: true,
  },
  meeting: {
    type: Object as PropType<DeepReadonly<Meeting>>,
    default: undefined,
  },
  currentSlideIndex: {
    type: Number,
    required: true,
  },
  slideRefreshKey: {
    type: Number,
    required: true,
  },
  firstValidSlide: {
    type: Number,
    required: true,
  },
  lastValidSlide: {
    type: Number,
    required: true,
  },
  uuid: {
    type: String,
    default: null,
  },
  embedDeck: {
    type: Object as PropType<DeepReadonly<DeckWithEmbed>>,
    default: null,
  },
  isContentBeingChosen: {
    type: Boolean,
    default: false,
  },
  isContentBeingShared: {
    type: Boolean,
    default: false,
  },
  isGridMode: {
    type: Boolean,
    default: false,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
  isAutoPlaying: {
    type: Boolean,
    default: false,
  },
  user: {
    type: Object as PropType<DeepReadonly<DeckUser>>,
    default: undefined,
  },
  panZoomTransform: {
    type: Object as PropType<PanZoomTransform>,
    default: () => ({ css: {}, dimensions: null }),
  },
  slideViewType: {
    type: String as PropType<SlideType>,
    default: null,
  },
  site: {
    type: Object as PropType<DeepReadonly<SiteState>>,
    default: () => ({}),
  },
  isImageHotspotDescriptionOpen: {
    type: Boolean,
    default: false,
  },
  currentPdfPage: {
    type: Number,
    default: 0,
  },
  tileRowSize: {
    type: String as PropType<TileRowSize>,
    default: 'sm',
  },
  nonShareableMeeting: {
    type: Boolean,
    default: false,
  },
  controls: {
    type: Object as PropType<ReadonlyControls>,
    default: null,
  },
  threeDViewerError: {
    type: String,
    default: null,
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
})

const emit = defineEmits([
  'annotate',
  'analytics-fired',
  'dismiss-embed-overlay',
  'exit',
  'goto:hotspot',
  'navigation-next-slide',
  'navigation-previous-slide',
  'on-mounted',
  'start-auto-play',
  'pause-autoplay',
  'refetch-file',
  'resume-autoplay',
  'set-current-slide-index',
  'site-camera-change',
  'slide-ready',
  'toggle-image-hotspot-description',
  'update:annotationSubscriber',
  'update:image-dimensions',
  'update:site-camera',
  'update:site-controls',
  'update:site-observer',
  'update:slideContainer',
])

const DECK_MOBILE_WIDTH = 768
const { $analytics, $sentry } = useNuxtApp()
const { alerts, hideAlert } = useAlerts()
const { contextMenu } = useContextMenu()
const { currentSlideIndex, currentPdfPage, panZoomTransform, deck, embedDeck, slideViewType } = toRefs(props)

const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))
globalFetchHeaders.value['X-Deck-Id'] = `${props.deck.id}`

const isSlideLoading: Ref<boolean> = ref(true)
const noWebGLDetected: Ref<boolean> = ref(false)
const isSidebarOpen: Ref<boolean> = ref(!props.embedDeck && window.innerWidth > DECK_MOBILE_WIDTH)
const animating: Ref<boolean> = ref(false)
const isSlideTransitioning: Ref<boolean> = ref(false)

const slide = computed(() => props.deck.slides[props.currentSlideIndex])
const userType = computed(() => props.user?.deckUserType)
const captionsEnabled = computed(() => (!props.meeting || props.meeting.is_caption_enabled) && slide.value)
const siteHotspotType = computed(() => props.site.selectedHotspot?.type ?? '')
const showCaptions = computed(() => {
  const defaultConditions =
    // do not show for Meeting (E), Video (V), or Header (H) slides
    'SIPMD'.includes(slide.value?.type) &&
    !props.isGridMode &&
    !props.isContentBeingShared &&
    siteHotspotType.value !== '3' &&
    !!slide.value.description &&
    !isSlideLoading.value

  if (slide.value?.type === 'M') {
    return defaultConditions && props.threeDViewer.progress.value === 1
  }

  return defaultConditions
})

const { state: userState } = useUser()

const { toggleFullscreen } = useFullscreenHelpers({ deck })
usePreloadPdfImages({ deck, currentSlideIndex, isEmbedded: computed(() => !!embedDeck.value) })
usePreloadSiteImages({ deck, uuid: props.uuid })
useDetectWebGL({ deck, onNoWebGL: () => (noWebGLDetected.value = true) })

const annotations = reactive<{
  container: HTMLElement | undefined
  active: boolean
  aspect: number | undefined
  lineWidth: number | undefined
  stroke: AnnotationColors | undefined
  zoom: number
  pan: Point
  uvCoords: Point | null
  reset(): void
  undo(): void
}>({
  container: undefined,
  active: false,
  aspect: undefined,
  lineWidth: undefined,
  stroke: undefined,
  zoom: 1,
  pan: { x: 0, y: 0 },
  uvCoords: null,
  reset() {},
  undo() {},
})
useSetAnnotationsFromPanZoom({
  panZoomTransform,
  slide,
  updateSiteAnnotations: payload => Object.assign(annotations, payload),
})
const annotationsDisabled = computed(() => isSlideLoading.value || animating.value)
const annotationsObjectFit = computed(() => ('SM'.includes(slideViewType.value) ? 'cover' : 'contain'))
const renderAnnotationsToPortal = computed(
  () => slide.value?.type === 'S' && (slideViewType.value === 'I' || (slideViewType.value === 'P' && userType.value === 'controlled')),
)

function handleExitDeck() {
  $analytics.track(`${props.analyticsEventName}LeavePresentationButtonPressed`)
  $analytics.track(`${props.analyticsEventName}ExitedSlide`, {
    slideId: slide.value?.id,
    slideIndex: props.currentSlideIndex,
    deckId: props.deck.id,
    uuid: props.uuid,
  })

  emit('exit')
}

function refetchFile() {
  emit('refetch-file')
}

function onGoToHotspot(hotspot: SelectedHotspot | null) {
  annotations.active = false
  reset()

  emit('goto:hotspot', hotspot)
}

function onToggleFullscreen() {
  try {
    toggleFullscreen()
  } catch (error) {
    $sentry?.captureException(error)
  }
}

function onSlideReady(isReady: boolean) {
  emit('slide-ready', isReady)
  isSlideLoading.value = !isReady
}

// Don't reset if controlled user. Let host control it.
const reset = () => userType.value !== 'controlled' && annotations.reset()

watch([currentSlideIndex, () => props.site?.siteId], () => {
  annotations.reset()
  annotations.active = false
})

watch(currentPdfPage, () => annotations.reset())

watch(toRef(annotations, 'container'), container => emit('update:slideContainer', container), { immediate: true })

watch(isSidebarOpen, (isSidebarOpen: boolean) => {
  if (userType.value === 'controlled') return

  if (isSidebarOpen) {
    document.body.classList.add('overflow-x-hidden')
  } else {
    document.body.classList.remove('overflow-x-hidden')
  }
})

watch(
  () => props.isAutoPlaying,
  (isAutoPlaying: boolean) => (isSidebarOpen.value = !isAutoPlaying),
)

// Ensures the loading state is set to true whenever users navigate to a different slide
watch(slide, () => (isSlideLoading.value = true))

type AnnotationMessages = Extract<
  RemoteControlMessagesOut,
  | RemoteControlMessagesOut.AnnotationActive
  | RemoteControlMessagesOut.AnnotationStroke
  | RemoteControlMessagesOut.AnnotationReset
  | RemoteControlMessagesOut.AnnotationUndo
>
const messageHandlers: Record<AnnotationMessages, (payload: any) => void> = {
  [RemoteControlMessagesOut.AnnotationActive]: (payload: boolean) => {
    annotations.active = payload
  },
  [RemoteControlMessagesOut.AnnotationStroke]: (payload: number) => {
    annotations.stroke = payload
  },
  [RemoteControlMessagesOut.AnnotationReset]: () => {
    annotations.reset()
  },
  [RemoteControlMessagesOut.AnnotationUndo]: () => {
    annotations.undo()
  },
}
function remoteControlMessageHandler(event: MessageEvent<{ name: AnnotationMessages; payload: any }>) {
  const { origin, source, data } = event
  if (origin !== window.location.origin) return
  if ((source as WindowProxy)?.name !== 'RemoteDeckControls') return
  const { name, payload } = data
  if (name in messageHandlers) {
    messageHandlers[name](payload)
  }
}

onMounted(() => {
  emit('on-mounted')

  if (props.analyticsEventName !== 'PartnerEmbed' && !props.analyticsHaveFired?.deckPageLoad) {
    $analytics.track(`${props.analyticsEventName}PageLoad`, {
      deckId: props.deck.id,
      deckName: props.deck.title,
      companyId: props.deck.company.id,
      companyName: props.deck.company.name,
      uuid: props.uuid,
      partnerId: props.embedDeck?.partner_id,
      partnerName: props.embedDeck?.partner_name,
      embedLocation: props.embedDeck?.embed_name,
      shareCode: props.meeting?.share_id,
      triggeredBy: (() => {
        switch (userType.value) {
          case 'host':
            return 'host'
          case 'controlled':
            return 'guest'
          default:
            return undefined
        }
      })(),
    })

    emit('analytics-fired', 'deckPageLoad')
  }

  window.addEventListener('message', remoteControlMessageHandler)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', remoteControlMessageHandler)
})

onUnmounted(() => {
  document.body.classList.remove('overflow-x-hidden')

  if (props.embedDeck?.id) return

  delete globalFetchHeaders.value['X-Deck-Id']
})

const contentBackground = computed(() => {
  if (slide.value?.type === 'S' || slide.value?.type === 'M') {
    return 'content-bg-dark'
  }

  return 'content-bg-light'
})

useHead({
  title: () => `${props.deck.title} - ${props.deck.company.name} | VRIFY`,
  meta: [
    {
      property: 'og:title',
      content: () => `${props.deck.title} - ${props.deck.company.name} | VRIFY`,
    },
    {
      property: 'og:image',
      content: () => props.deck.company?.profile_image ?? '',
    },
    {
      property: 'og:url',
      content: () => `${config.public.WEB_ENDPOINT}companies/${props.deck.company?.url_slug}`,
    },
  ],
})
</script>

<style lang="stylus" scoped>
@css {
  .deck {
    --sidebar-width: 15.0625rem; /* for browsers that do not support min() */
    --sidebar-width: min(15.0625rem, calc(100vw - 44px));; /* 44px is width of toggle control */
    --sidebar-duration: 0.2s
  }
}

.deck
  max-height: stretch // Needed so iOS browsers do not overflow

  .content
    // Delay the width transition until the end of the animation. Changing width is expensive
    transition width 1ms var(--sidebar-duration), transform var(--sidebar-duration)

    .layer
      @apply absolute w-full h-full inset-0

  .sidebar-transition
    transition transform var(--sidebar-duration)

    width var(--sidebar-width)
    max-width var(--sidebar-width)
    transform translateX(calc(0px - var(--sidebar-width)))

  &.open
    .content
      transform translateX(calc(0px + var(--sidebar-width)))
      width calc(100% - var(--sidebar-width))

    .sidebar-transition
      transform translateX(0)

  &.recording
    .content
      width 100%
      transform translateX(0)

    .overlay-container
      opacity 0
      pointer-events none

    .sidebar-transition
      display none

.content-bg-light
  @apply bg-white

.content-bg-dark
  background radial-gradient(#4a4a4a, #212121)

.screen-share-overlay
  @apply absolute inset-0 bg-black bg-opacity-50 z-30 pointer-events-none

.fade-enter-active,
.fade-leave-active
  transition: opacity 0.2s ease-out

.fade-enter-from, .fade-leave-to
  opacity: 0

.slide-container > *
  @apply absolute inset-0
</style>
